import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

export interface OnsiteConfig {
  enabled: boolean
  cash_enabled: boolean
  /** Seuil du service en salle, en centimes. Décidé côté serveur. */
  table_service_min_minor: number
  currency: string
  /** « Je n'ai pas de table » est-il proposé ? Réglage `onsite.allow_no_table`. */
  allow_no_table: boolean
}

export interface OnsiteSelection {
  kind: 'food_item' | 'food_pack' | 'drink'
  ref_id: string
  quantity: number
}

export interface OnsiteOrderRequest {
  /** Absent : « je n'ai pas de table » — le prénom est alors exigé. */
  table_number?: string
  customer_label?: string
  spot_note?: string
  items: OnsiteSelection[]
  payment_method: 'paypal' | 'cash'
  cash_tendered_minor?: number
  note?: string
  locale: string
}

export interface OnsiteLine {
  label: string
  quantity: number
  unit_price_minor: number
  line_total_minor: number
  station: string
}

export interface OnsiteOrder {
  order_id: string
  order_number: string
  status: string
  table_number: string
  /** Le nom donné à la table en administration (« Champagne »). */
  table_name: string | null
  currency: string
  total_minor: number
  lines: OnsiteLine[]
  /** `table_service` ou `counter_pickup` — le serveur tranche, pas nous. */
  service_mode: string
  table_service_min_minor: number
  payment_method: string
  change_due_minor: number | null
  approval_url: string | null
  /** PayPal sans API : à recopier dans le message du paiement. */
  payment_reference?: string | null
  access_token: string
  /** Le prénom du serveur qui viendra encaisser, si la table en a un. */
  waiter_first_name?: string | null
  /** Sans table : le prénom donné (et `table_number` est vide). */
  customer_label?: string | null
  spot_note?: string | null
  /** Où en est la commande — calculé par le serveur, sur les lignes. */
  prep_state?: 'unpaid' | 'preparing' | 'ready' | 'cancelled'
}

/** La table telle que la salle la connaît : l'invité la confirme avant tout. */
export interface OnsiteTable {
  table_number: string
  name: string | null
  waiter_first_name: string | null
}

export interface OnsiteQuote {
  currency: string
  lines: {
    kind: string
    ref_id: string
    label: string
    quantity: number
    unit_price_minor: number
    line_total_minor: number
    station: string
  }[]
  total_minor: number
  items_count: number
}

export function useOnsiteConfig(slug: string | undefined) {
  return useQuery({
    queryKey: ['onsite-config', slug],
    queryFn: () => apiClient.get<OnsiteConfig>(`/public/events/${slug}/onsite/config`),
    enabled: Boolean(slug),
    // La commande s'ouvre au micro, en cours de soirée : la page doit le voir
    // sans qu'on demande à trois cents personnes de recharger.
    refetchInterval: 30_000,
  })
}

export function useCreateOnsiteOrder(slug: string | undefined) {
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: (payload: Omit<OnsiteOrderRequest, 'locale'>) =>
      apiClient.post<OnsiteOrder>(`/public/events/${slug}/onsite/orders`, {
        ...payload,
        locale: i18n.language,
      }),
  })
}

/**
 * Vérifie le numéro saisi (ou lu sur le QR) contre le plan de salle.
 *
 * Une mutation plutôt qu'une requête : c'est un geste — « c'est ma table » —
 * pas une donnée qu'on tient à jour.
 */
export function useLookupTable(slug: string | undefined) {
  return useMutation({
    mutationFn: (tableNumber: string) =>
      apiClient.get<OnsiteTable>(
        `/public/events/${slug}/onsite/tables/${encodeURIComponent(tableNumber)}`,
      ),
  })
}

/**
 * Le total du panier, calculé par le serveur.
 *
 * Le panier fait partie de la clé : une réponse arrivée en retard ne peut pas
 * afficher le total d'un panier qui n'est plus celui à l'écran.
 */
export function useOnsiteQuote(slug: string | undefined, items: OnsiteSelection[]) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['onsite-quote', slug, i18n.language, items],
    queryFn: () =>
      apiClient.post<OnsiteQuote>(`/public/events/${slug}/onsite/quote`, {
        items,
        locale: i18n.language,
      }),
    enabled: Boolean(slug) && items.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

/**
 * Suivre sa commande : le statut, relu toutes les dix secondes.
 *
 * L'invité sans table attend, téléphone en main, qu'on lui dise de venir la
 * chercher : la page doit passer à « prête » sans qu'il recharge. Le suivi
 * s'arrête quand il n'y a plus rien à attendre (prête, annulée).
 */
export function useOnsiteOrderStatus(orderId: string, token: string) {
  return useQuery({
    queryKey: ['onsite-order', orderId, token],
    queryFn: () =>
      apiClient.get<OnsiteOrder>(
        `/public/onsite/orders/${orderId}?token=${encodeURIComponent(token)}`,
      ),
    refetchInterval: (query) => {
      const state = query.state.data?.prep_state
      return state === 'ready' || state === 'cancelled' ? false : 10_000
    },
    retry: false,
  })
}
